import type { ReactNode } from 'react'
import { Box, Button, Checkbox, FormControlLabel, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { FileChip, StatusPill, StepCard, StepTitle } from '../../components/ui'
import { miscStateName } from '../../data/miscHandledStates'
import { portalRulesForState, platformRulesForStates } from '../../data/stateLoginRules'
import {
  credentialPersonName,
  credentialRowsForPortal,
  entityPortals,
  isCredentialRowComplete,
  platformRequirement,
  portalLabelForRecord,
  statePortalRequirement,
} from '../../lib/credentials'
import { entityPercent, entityStatus } from '../../lib/completion'
import { SHOW_TAX_PAYMENT_CONTACT } from '../../lib/prototypeSettings'
import { clientLocation, entityLocation } from '../flow'
import type { BusinessEntity, EntitySectionId, WizardLocation } from '../../types/onboarding'

const th = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap', py: 1 } as const
const td = { fontSize: 13, color: '#1e293b', py: 0.75, verticalAlign: 'top' } as const
const dash = <Box component="span" sx={{ color: '#94a3b8' }}>—</Box>

function Section({ title, onEdit, children }: { title: string; onEdit?: () => void; children: ReactNode }) {
  return (
    <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
        {onEdit ? (
          <Button type="button" size="small" onClick={onEdit}>
            Edit
          </Button>
        ) : null}
      </Box>
      {children}
    </Box>
  )
}

function MiniTable({ head, rows, empty }: { head: string[]; rows: ReactNode[][]; empty: string }) {
  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            {head.map((h) => (
              <TableCell key={h} sx={th}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={head.length} sx={{ ...td, color: '#64748b' }}>
                {empty}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((cells, i) => (
              <TableRow key={i}>
                {cells.map((c, j) => (
                  <TableCell key={j} sx={td}>
                    {c}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  )
}

/** Per-entity summary: the data MISC needs to build the license matrix and login sheet. */
export function EntityReview({ entity, onEdit }: { entity: BusinessEntity; onEdit?: (section: EntitySectionId) => void }) {
  const platforms = platformRulesForStates(entity.statesToHandle)

  const matrixRows = entity.statesToHandle.map((code) => {
    const answer = entity.writingBusiness[code]
    const drlps = entity.drlps.filter((d) => d.assignedStates.includes(code)).map((d) => d.fullName || 'Unnamed DRLP')
    const systems = portalRulesForState(code)
    const loginCells = systems.length
      ? systems.map((rule) => {
          const req = statePortalRequirement(rule, answer)
          const rows = credentialRowsForPortal(entity, rule.id)
          const done = rows.filter(isCredentialRowComplete).length
          const status = !req ? 'Unanswered' : req === 'notNeeded' ? 'Not needed' : `${done}/${rows.length} provided`
          return `${rule.system}: ${status}`
        })
      : ['Via multi-state platform']
    return [
      miscStateName(code),
      answer === 'yes' ? 'Yes' : answer === 'no' ? 'No' : dash,
      drlps.length ? drlps.join(', ') : <Box component="span" sx={{ color: '#b45309', fontWeight: 600 }}>None assigned</Box>,
      <Box>{loginCells.map((l) => <div key={l}>{l}</div>)}</Box>,
    ]
  })

  const platformRows = platforms.map((rule) => {
    const req = platformRequirement(entity, rule)
    const rows = credentialRowsForPortal(entity, rule.id)
    const done = rows.filter(isCredentialRowComplete).length
    return [rule.stateLabel, !req ? 'Unanswered' : req === 'notNeeded' ? 'Not needed' : `${done}/${rows.length} provided`]
  })

  const requiredPortalIds = new Set(
    entityPortals(entity)
      .filter((p) => p.requirement === 'required' || p.requirement === 'optional')
      .map((p) => p.rule.id),
  )
  const credentialRows = entity.credentials
    .filter((c) => requiredPortalIds.has(c.portalId))
    .map((c) => [
      portalLabelForRecord(c),
      c.recordType === 'entity' ? 'Entity' : c.recordType === 'drlp' ? 'DRLP' : 'Individual',
      credentialPersonName(entity, c) || dash,
      c.username || dash,
      c.password ? '••••••••' : dash,
      isCredentialRowComplete(c) ? 'Yes' : <Box component="span" sx={{ color: '#b45309', fontWeight: 600 }}>Missing</Box>,
    ])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{entity.name || 'Unnamed entity'}</Typography>
        <StatusPill status={entityStatus(entity)} />
        <Typography sx={{ fontSize: 13, color: '#475569' }}>{entityPercent(entity)}% complete</Typography>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>States, DRLPs &amp; logins</Typography>
          {onEdit ? <Button size="small" onClick={() => onEdit('states')}>Edit states</Button> : null}
        </Box>
        <MiniTable head={['State', 'Writing business', 'DRLP(s)', 'Logins']} rows={matrixRows} empty="No states selected." />
        {platformRows.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <MiniTable head={['Multi-state platform', 'Logins']} rows={platformRows} empty="" />
          </Box>
        ) : null}
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>DRLPs</Typography>
          {onEdit ? <Button size="small" onClick={() => onEdit('drlp')}>Edit DRLPs</Button> : null}
        </Box>
        <MiniTable
          head={['Full name', 'Job title', 'Resident state', 'SL license #', 'NPN', 'Assigned states']}
          rows={entity.drlps.map((d) => [
            d.fullName || dash,
            d.jobTitle || dash,
            d.residentState || dash,
            d.slLicenseNumber || dash,
            d.npn || dash,
            d.assignedStates.length ? d.assignedStates.join(', ') : dash,
          ])}
          empty="No DRLPs entered."
        />
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>Individual licensees</Typography>
          {onEdit ? <Button size="small" onClick={() => onEdit('individuals')}>Edit individuals</Button> : null}
        </Box>
        <MiniTable
          head={['Full name', 'Resident state', 'License #', 'NPN']}
          rows={entity.individuals.map((p) => [p.fullName || dash, p.residentState || dash, p.licenseNumber || dash, p.npn || dash])}
          empty="No individual licensees entered."
        />
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 }}>Credentials provided</Typography>
          {onEdit ? <Button size="small" onClick={() => onEdit('credentials')}>Edit credentials</Button> : null}
        </Box>
        <MiniTable
          head={['System', 'Type', 'Name', 'Username', 'Password', 'Complete']}
          rows={credentialRows}
          empty="No logins required yet."
        />
      </Box>
    </Stack>
  )
}

/** Design manual p.11 — Review & Submit. */
export function StepReview() {
  const { state, dispatch, goTo } = useOnboarding()
  const edit = (loc: WizardLocation) => () => goTo(loc)

  return (
    <StepCard>
      <StepTitle subtitle="Review your information below. You can edit any section before final submission.">
        Review Your Information
      </StepTitle>

      <Stack spacing={3}>
        <Section title="Business Entities" onEdit={edit(clientLocation('entitySetup'))}>
          <MiniTable
            head={['Entity', 'Resident SL state', 'SL license #', 'NPN', 'FEIN']}
            rows={state.entities.map((e) => [e.name || dash, e.residentState || dash, e.slLicenseNumber || dash, e.npn || dash, e.fein || dash])}
            empty="No entities entered."
          />
        </Section>

        {state.entities.map((e, i) => (
          <Section key={e.id} title={`Entity ${i + 1} of ${state.entities.length}`}>
            <EntityReview entity={e} onEdit={(section) => goTo(entityLocation(e.id, section))} />
          </Section>
        ))}

        <Section title="Contacts" onEdit={edit(clientLocation('contacts'))}>
          <MiniTable
            head={['Question', 'Email address(es)']}
            rows={[
              ['Monthly service invoice', state.contacts.invoiceEmails || dash],
              ['Client admin (Surplus Lines Tax Portal)', state.contacts.portalAdminEmails || dash],
              ...(SHOW_TAX_PAYMENT_CONTACT ? [['Surplus lines tax payments', state.contacts.taxPaymentEmails || dash]] : []),
            ]}
            empty=""
          />
        </Section>

        <Section title="Attestation" onEdit={edit(clientLocation('attestation'))}>
          <Typography sx={{ fontSize: 13 }}>
            {state.attestation.checked
              ? 'Confirmed by checkbox.'
              : state.attestation.initials.trim()
                ? `Initialed: ${state.attestation.initials.toUpperCase()}`
                : 'Not yet confirmed.'}
          </Typography>
        </Section>

        <Section title="OTR Submission" onEdit={edit(clientLocation('otr'))}>
          {state.otrUpload ? <FileChip file={state.otrUpload} /> : <Typography sx={{ fontSize: 13 }}>Not uploaded.</Typography>}
        </Section>

        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Final Submission</Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={state.certified} onChange={(e) => dispatch({ type: 'SET_CERTIFIED', value: e.target.checked })} />}
            label={
              <Typography sx={{ fontSize: 15 }}>
                I certify that the information provided is accurate and complete to the best of my knowledge.
              </Typography>
            }
          />
          <Typography sx={{ mt: 0.5, fontSize: 13, color: '#64748b' }}>Select Send to MISC below to submit your onboarding package.</Typography>
        </Box>
      </Stack>
    </StepCard>
  )
}
